"use client";

import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";

import type { AstAdObj, AstNative } from "@omni/ads";
import type { CSSProperties } from "react";
import type { Ad as AdType } from "../../types/ads/adResource";
import type { ContentDisplayResource } from "../../types/content/newsfeedAdPlaceholder";

import { useIntersectionObserver } from "../../hooks/useIntersectionObserver/useIntersectionObserver";
import { isConsentToAll, useTcfPermission } from "../../tcf/helpers";
import { getIdentitySession } from "../../utils/auth/getSessionFromIdentity";
import { fssMaskManager } from "../../utils/fssMaskManager";
import { TeaserInjectableNative } from "../Teaser/TeaserInjectableNative/TeaserInjectableNative";
import styles from "./Ad.module.scss";
import { AdInfoWrapper } from "./Adinfo/AdInfoWrapper";
import { isConfiguredAndLoaded } from "./utils/constants";
import { getLayout } from "./utils/helpers";
import {
  adPlacementMap,
  parseAdPlacement,
} from "./utils/helpers/adPlacementMap";
import { getGeoTrackingPermission } from "./utils/helpers/getGeoTrackingPermission";
import { setCustomFullscreenStyles } from "./utils/helpers/setCustomFullscreenStyles";
import { configureAndLoadAdPlacement } from "./utils/xandr";

type AdResource = AdType & Partial<Omit<ContentDisplayResource, "type">>;

type AdProps = {
  resource: AdResource;
  initialSize?: {
    height: number | string;
    width: number | string;
  };
  isInjectableNative?: boolean;
  takeoverAvailable?: () => void;
  onFailure?: () => void;
  notAvailable?: () => void;
  loadAdRightAway?: boolean;
  hasDynamicSize?: boolean;
  shouldKeepSizeOnCollapse?: boolean;
  setTakeoverCreativeId?: React.Dispatch<React.SetStateAction<string>>;
  setTakeoverIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

const captureProcentVHOrPxRegex = /\d+(\.\d+)?\s?(%|vh|px)/g;

const getTeaserLayout = (resource: AdResource) => {
  const view =
    Array.isArray(resource.placementRules) &&
    resource.placementRules.length > 0 &&
    Array.isArray(resource.placementRules[0].rules.views)
      ? resource.placementRules[0].rules.views[0]
      : "top";

  return view === "latest" ? "small" : "large";
};

const Ad: React.FC<AdProps> = ({
  resource,
  initialSize,
  onFailure,
  takeoverAvailable,
  notAvailable,
  isInjectableNative,
  loadAdRightAway = false,
  hasDynamicSize = false,
  setTakeoverCreativeId,
  setTakeoverIsLoading,
}) => {
  const [isViewed, setIsViewed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(resource.startAsCollapsed);
  const [isRefreshed, setIsRefreshed] = useState(false);
  const [isFullScreenScroll, setIsFullScreenScroll] = useState(false);
  const [shouldShowAd, setShouldShowAd] = useState(true);
  const [position, setPosition] = useState("");
  const [customStyles, setCustomStyles] = useState<CSSProperties>({});
  const [creativeId, setCreativeId] = useState<string>("");
  const [[width, height], setPlaceholderValues] = useState([
    initialSize?.width || null,
    initialSize?.height || null,
  ]);
  const [injectableIdentifier, setInjectableIdentifier] = useState("");
  const [canKeepSize, setCanKeepSize] = useState(false);
  const [nativeAd, setNativeAd] = useState<AstNative | undefined>(undefined);

  const fSSContainerRef = useRef<HTMLDivElement | null>(null);
  const feedElementRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const fssIdRef = useRef<string | null>(null); // Unique ID for this FSS ad instance

  const { placementContext, placementWebName, correct } = parseAdPlacement(
    resource.placement,
  );
  const shouldLoadAdRightAway = resource.loadAdRightAway ?? loadAdRightAway;

  const marketingConsent = useTcfPermission("CMP:marketing");
  const isInArticle =
    resource.placement === "article.afterLinks" ||
    resource.placement === "article.afterByline" ||
    resource.placement === "article.beforeLinks";

  function isTakeoverAdResource(placement?: string): boolean {
    if (typeof placement !== "string") {
      return false;
    }
    return !!placementWebName?.includes("takeover");
  }

  const isTakeover = isTakeoverAdResource(resource.placement);

  const id = `${isInjectableNative ? injectableIdentifier : position}`;

  const placement = resource.placement.split(".");

  const { ref, inView } = useIntersectionObserver({
    rootMargin: shouldLoadAdRightAway ? "0px" : "200px", // Don't preload if loading immediately
    threshold: 0,
  });

  const onAvailable = useCallback(
    (response: AstAdObj) => {
      if (
        response?.banner &&
        response.banner.height === 1080 &&
        response.banner.width === 1920
      ) {
        setCustomFullscreenStyles(response.targetId);
      }

      if (
        resource.placement === `feed.${placementWebName}` &&
        takeoverAvailable
      ) {
        takeoverAvailable();
      }
    },
    [resource.placement, takeoverAvailable, placementWebName],
  );

  const loadAd = useCallback(
    async (isReload: boolean = false): Promise<void> => {
      if (isConfiguredAndLoaded.has(id) && !isReload) {
        return;
      }

      try {
        // Parallelize independent operations (consent and session)
        const [consentToAll, { sig, userId }] = await Promise.all([
          isConsentToAll(),
          getIdentitySession(resource.user.isLoggedIn),
        ]);

        // GeoTracking depends on userId/sig from above
        const geoTracking = await getGeoTrackingPermission(
          resource.user.isLoggedIn,
          userId,
          sig,
        );

        const user = {
          isPremium: resource.user.isPremium,
          isLoggedIn: resource.user.isLoggedIn,
        };

        const filteredAdRule = resource.placementRules.filter((ad) =>
          ad.rules.position.startsWith(position),
        );

        if (!filteredAdRule.length && !isTakeover) {
          return resource.shouldKeepSizeOnCollapse
            ? setCanKeepSize(true)
            : setShouldShowAd(false);
        }

        const adRule = { ...filteredAdRule[0] };
        const article = resource.context?.article || {};

        const { stylesFromXandr, creativeId, nativeAd, isFSS } =
          await configureAndLoadAdPlacement({
            id,
            adRule,
            user,
            article,
            geoTracking,
            consentToAll,
            marketingConsent,
            onFailure,
            onAvailable,
            notAvailable,
          });

        const isNativeAd = !!nativeAd;
        if (isNativeAd) {
          setNativeAd({ ...nativeAd });

          if (creativeId) {
            setCreativeId(creativeId.toString());
          }
        }

        if (isFSS) {
          setIsFullScreenScroll(true);
        }

        if (!isNativeAd) {
          if (creativeId && !isTakeover) {
            setCreativeId(creativeId);
          }

          if (creativeId && isTakeover && setTakeoverCreativeId) {
            setTakeoverCreativeId(creativeId);
          }

          const xandrAdHeight = stylesFromXandr?.height
            ? !stylesFromXandr?.height.match(captureProcentVHOrPxRegex)
              ? parseInt(stylesFromXandr?.height, 10)
              : stylesFromXandr?.height
            : 0;

          const xandrAdWidth = stylesFromXandr?.width
            ? !stylesFromXandr?.width?.includes("%")
              ? parseInt(stylesFromXandr?.width, 10)
              : stylesFromXandr?.width
            : 0;
          setCustomStyles((prevStyles) => ({
            ...prevStyles,
            ...stylesFromXandr,
            ...(xandrAdHeight && { height: xandrAdHeight }),
            ...(xandrAdWidth && { width: xandrAdWidth }),
          }));

          isConfiguredAndLoaded.add(id);
        }

        isTakeover
          ? setTakeoverIsLoading && setTakeoverIsLoading(false)
          : setIsLoading(false);

        if (resource.startAsCollapsed) {
          setIsCollapsed(false);
        }
      } catch (e) {
        if (resource.shouldKeepSizeOnCollapse) {
          setCanKeepSize(true);
        } else {
          setShouldShowAd(false);
        }

        console.error(`Ad.tsx: Failed to load ad placement (id: ${id})`, e);
      }
    },
    [
      resource.placementRules,
      resource.user.isLoggedIn,
      resource.user.isPremium,
      resource.context.article,
      resource.shouldKeepSizeOnCollapse,
      resource.startAsCollapsed,
      id,
      position,
      marketingConsent,
      onFailure,
      notAvailable,
      onAvailable,
      isTakeover,
      setTakeoverCreativeId,
      setTakeoverIsLoading,
    ],
  );

  useEffect(() => {
    const eventListener = (event: MessageEvent) => {
      if (typeof event.data === "string" && event.data.includes("targetId")) {
        const { name, targetId } = JSON.parse(event.data);
        if (name === "hideAd" && id === targetId) {
          setShouldShowAd(false);
        }
      }
    };

    if (!isLoading && !resource.shouldKeepSizeOnCollapse) {
      window.addEventListener("message", eventListener);
      return () => window.removeEventListener("message", eventListener);
    }
  }, [id, isLoading, resource.shouldKeepSizeOnCollapse]);

  useEffect(() => {
    if (
      ((inView && position && !isViewed) || shouldLoadAdRightAway) &&
      correct
    ) {
      setIsViewed(true);
      loadAd();
    }
  }, [
    inView,
    loadAd,
    isViewed,
    position,
    correct,
    shouldLoadAdRightAway,
    placementContext,
    id,
  ]);

  useEffect(() => {
    if (correct && !isViewed) {
      const layout = getLayout();
      const brandName =
        process.env.NEXT_PUBLIC_UI_THEME === "ekonomi" ? "ekonomi" : "core";
      const position =
        adPlacementMap[placementContext][brandName][placementWebName]?.[layout];
      const placeholderWidth = resource.placeholderValues[layout]?.width;
      const placeholderHeight = resource.placeholderValues[layout]?.height;

      if (placeholderWidth && placeholderHeight) {
        setPlaceholderValues([placeholderWidth, placeholderHeight]);
      }

      if (isInjectableNative) {
        setInjectableIdentifier(resource.placementRules[0].rules.position);
      }

      if (position) {
        setPosition(position);
      } else {
        setShouldShowAd(false);
      }
    }
  }, [
    placementContext,
    placementWebName,
    resource.placeholderValues,
    correct,
    isViewed,
    resource,
    isInjectableNative,
    resource.placementRules,
    resource.placement,
  ]);

  useEffect(() => {
    if (!isLoading && hasDynamicSize) {
      setCustomStyles((currStyles) => ({
        ...currStyles,
        height: "auto",
        width: "100%",
      }));
    }
  }, [hasDynamicSize, isLoading]);

  useEffect(() => {
    if (
      inView &&
      isViewed &&
      isLoading &&
      !isRefreshed &&
      (isConfiguredAndLoaded.has(id) || injectableIdentifier)
    ) {
      setIsRefreshed(true);
      loadAd(true);
    }
  }, [
    inView,
    isLoading,
    isViewed,
    id,
    isRefreshed,
    injectableIdentifier,
    loadAd,
  ]);

  // Find and cache feed element early (but don't register yet)
  useEffect(() => {
    if (!ref.current || feedElementRef.current || getLayout() !== "mobile") {
      return;
    }

    const fSSWrapper = ref.current;

    // Find feed element
    let element = fSSWrapper.parentElement;
    while (element && !element.className.includes("feed")) {
      element = element.parentElement;
    }

    if (element) {
      feedElementRef.current = element;
      // Generate unique ID for this ad instance
      fssIdRef.current = `fss-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
  }, [ref]);

  // Register FSS position with manager AFTER we know it's FSS
  useEffect(() => {
    if (
      !isFullScreenScroll ||
      !ref.current ||
      !feedElementRef.current ||
      !fssIdRef.current
    ) {
      return;
    }

    const fSSWrapper = ref.current;
    const feedElement = feedElementRef.current;

    const calculateAndRegisterPosition = () => {
      const feedRect = feedElement.getBoundingClientRect();
      const wrapperRect = fSSWrapper.getBoundingClientRect();

      // FSS wrapper will be 100vw x 100vh, so use viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const top = wrapperRect.top - feedRect.top;
      const left = wrapperRect.left - feedRect.left;
      const right = left + viewportWidth;
      const bottom = top + viewportHeight;

      // Register this FSS position with the manager
      fssMaskManager.registerFSS(feedElement, {
        id: fssIdRef.current!,
        top,
        left,
        right,
        bottom,
      });
    };

    // Wait for next frame to ensure .fSSWrapper class styles are applied
    requestAnimationFrame(() => {
      calculateAndRegisterPosition();

      // Setup ResizeObserver to recalculate on layout changes
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          calculateAndRegisterPosition();
        });
      });

      resizeObserverRef.current.observe(feedElement);
      resizeObserverRef.current.observe(fSSWrapper);
    });

    return () => {
      if (feedElementRef.current && fssIdRef.current) {
        fssMaskManager.unregisterFSS(feedElementRef.current, fssIdRef.current);
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [isFullScreenScroll, ref]);

  return (
    <div
      ref={ref}
      className={cn(
        isFullScreenScroll ? styles.fSSWrapper : styles.adContainer,
        isTakeover && styles.takeoverWrapper,
      )}
    >
      {shouldShowAd && resource.havePlacementRule ? (
        <>
          {nativeAd ? (
            <div
              id={id}
              className={cn(isCollapsed && styles.collapsed)}
              data-testid="ad-native-component"
            >
              <TeaserInjectableNative
                ad={nativeAd}
                isLoggedIn={resource.user.isLoggedIn}
                creativeId={creativeId}
                layout={getTeaserLayout(resource)}
              />
            </div>
          ) : (
            <>
              {isTakeover || (isInArticle && isCollapsed) ? null : (
                <AdInfoWrapper
                  isLoading={isLoading}
                  isLoggedIn={resource.user.isLoggedIn}
                  creativeId={creativeId}
                  placementClassName={`${Array.isArray(placement) && !isFullScreenScroll ? placement[1] : isFullScreenScroll ? "fullscreen" : ""}`}
                />
              )}
              <div
                data-testid="ad-component"
                style={isTakeover ? { height: "100%" } : {}}
                className={cn([
                  styles.placement,
                  isInArticle && styles.articleResource,
                  (isFullScreenScroll || isInjectableNative || isTakeover) &&
                    styles.wideWrapper,
                  isCollapsed && styles.collapsed,
                  canKeepSize && styles.keepSizeButHidden,
                ])}
              >
                <div
                  style={
                    isInArticle
                      ? { width: customStyles?.width }
                      : isTakeover
                        ? { height: "100%" }
                        : {}
                  }
                  className={cn([
                    styles.wrapper,
                    isInArticle && styles.articleWrapper,
                  ])}
                >
                  <div
                    id={id}
                    ref={fSSContainerRef}
                    style={{
                      ...customStyles,
                      height: customStyles?.height || height || 0,
                      width: customStyles?.width || width || 0,
                    }}
                    className={cn([
                      isFullScreenScroll && styles.fSSContainer,
                      isLoading ? styles.loader : "",
                      hasDynamicSize && !isLoading && styles.dynamicSizing,
                      isTakeover && styles.takeoverContainer,
                    ])}
                  />
                </div>
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export { Ad };
export type { AdResource };
